<template>
  <div v-if="documentData">
    <ExternalSignContent :documentData="documentData" />
    <div class="my-4">
      <div>{{ t("process.comment") }}</div>
      <textarea
        v-auto-resize
        v-model="comment"
        class="w-full border-gray-300 rounded-md"
        rows="2"
      ></textarea>
    </div>

    <div class="flex items-center mt-3 gap-2">
      <button
        v-if="documentData.allowedActions.canSign"
        class="bg-primary-300 px-2 py-1 rounded-md text-white"
        @click="signDocument"
      >
        {{ t("sign.sign") }}
      </button>

      <button
        v-if="documentData.allowedActions.canRework"
        class="bg-yellow-600 px-2 py-1 rounded-md text-white"
        @click="reworkDocument"
      >
        {{ t("sign.rework") }}
      </button>

      <button
        v-if="documentData.allowedActions.canDeny"
        class="bg-red-600 px-2 py-1 rounded-md text-white"
        @click="denyDocument"
      >
        {{ t("sign.deny") }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { baseSignApi } from "@/config/http-sign";
import ExternalSignContent from "@/components/externalSign/ExternalSignContent.vue";
import { createCmsSignatureFromBase64 } from "@/utils/ncaLayer";
import { useToast } from "vue-toastification";
const toast = useToast();
import { t } from "@/i18n";

const route = useRoute();

const documentData = ref(null);
const comment = ref("");

const showError = (error) => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Неизвестная ошибка";

  toast.error(message);
};

onMounted(() => {
  fetchDocument();
});

const fetchDocument = () => {
  comment.value = "";
  baseSignApi
    .get(`/public/external-sign/${route.params.token}`)
    .then((response) => {
      documentData.value = response.data.data;
    })
    .catch(showError);
};

const signByEcp = async () => {
  const firstFile = documentData.value?.files?.[0];
  if (!firstFile?.fileGuid) {
    toast.error(t("auth.ncaError"));
    return;
  }

  try {
    const manifestResponse = await baseSignApi.get(
      `/public/external-sign/${route.params.token}/sign-manifest`
    );
    const manifestBase64 = manifestResponse.data.data;
    const cmsBase64 = await createCmsSignatureFromBase64(manifestBase64);

    const response = await baseSignApi.post(
      `/public/external-sign/${route.params.token}/sign/cms`,
      {
        fileGuid: firstFile.fileGuid,
        fileName: "NCAManifest.cms",
        cmsBase64,
      }
    );

    if (response.data.status) {
      toast.success(t("sign.success"));
      fetchDocument();
    } else {
      showError(response);
    }
  } catch (error) {
    showError(error);
  }
};

const signDocument = () => {
  signByEcp();
};

const denyDocument = () => {
  if (!comment.value) {
    toast.warning(t("sign.enter_comment"));
    return;
  }
  baseSignApi
    .post(`/public/external-sign/${route.params.token}/deny`, {
      comment: comment.value,
    })
    .then((response) => {
      if (response.data.status) {
        toast.success(t("sign.denied"));
        fetchDocument();
      } else {
        showError(response);
      }
    })
    .catch(showError);
};

const reworkDocument = () => {
  if (!comment.value) {
    toast.warning(t("sign.enter_comment"));
    return;
  }
  baseSignApi
    .post(`/public/external-sign/${route.params.token}/rework`, {
      comment: comment.value,
    })
    .then((response) => {
      if (response.data.status) {
        toast.success(t("sign.reworked"));
        fetchDocument();
      } else {
        showError(response);
      }
    })
    .catch(showError);
};
</script>
